/**
 * homepageConfigClient — read/write the per-agent Workspace Homepage config
 * via the CMHKAgentPlatform Gateway (GET/POST /channels/portal/workspace-homepage).
 *
 * The interface (`getConfig` / `saveConfig`) is intentionally async + narrow so
 * the UI is agnostic to the transport. `saveConfig` carries `version` for the
 * backend's optimistic-concurrency check; on a stale write the Gateway returns
 * 409 (GatewayApiError) which the caller surfaces as a reload prompt.
 */
import { gatewayClient } from '../../client/GatewayClient';
import type { HomepageConfig } from './types';

const PATH = '/channels/portal/workspace-homepage';

let uid = 0;
const genId = (p: string) => `${p}-${Date.now().toString(36)}-${(uid++).toString(36)}`;

/** Load an agent's homepage config. The Gateway seeds defaults (version 0) for
 *  an agent that has never been configured. */
export async function getConfig(agent: string): Promise<HomepageConfig> {
  return gatewayClient.get<HomepageConfig>(PATH, { agent });
}

/** Persist the whole config for an agent. Sends the current `version` for the
 *  optimistic-concurrency guard and returns the saved config (with the bumped
 *  version). Throws GatewayApiError(409) if the stored version has moved on. */
export async function saveConfig(config: HomepageConfig): Promise<HomepageConfig> {
  return gatewayClient.post<HomepageConfig>(PATH, {
    agent: config.agent,
    part1: config.part1,
    part2: config.part2,
    part3: config.part3,
    version: config.version ?? 0,
  });
}

export const newQuestionId = () => genId('q');
export const newTopicId = () => genId('t');
